import { fetchAgg } from '../../../../common/utils/fetch-agg';
import {
	teamsWorkspaceFilterOptionsOperationName,
	teamsWorkspaceFilterOptionsQueryGenerator,
	type ThirdPartySpaceFilterOptionsQueryVariables,
} from '../query-generator';
import type { TeamsWorkspaceFilterOptionsData } from '../types';

// Computed once at module load — the query string has no dynamic parts
const TEAMS_WORKSPACE_GRAPH_QUERY = teamsWorkspaceFilterOptionsQueryGenerator();

export type MSTeamsWorkspace = {
	id?: string;
	name: string;
};

export interface MSTeamsWorkspacesProps {
	cloudId: string;
	aggAbsoluteUrl?: string;
	headers?: Record<string, string>;
}

const TEAMS_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const TEAMS_CONVERSATION_ENTITY_ATI = 'ati:cloud:graph:conversation';
const MAX_WORKSPACE_RESULTS = 30;

/**
 * Fetches MS Teams workspace (team) names for use in the team name filter dropdown.
 * Uses the existing search AGG endpoint with ExternalConversation.workspace field,
 * following the same pattern as the Google Drive space filter.
 */
export const getMSTeamsWorkspaces = async ({
	cloudId,
	aggAbsoluteUrl,
	headers,
}: MSTeamsWorkspacesProps): Promise<MSTeamsWorkspace[]> => {
	const response = await fetchAgg<
		ThirdPartySpaceFilterOptionsQueryVariables,
		TeamsWorkspaceFilterOptionsData
	>({
		graphQuery: TEAMS_WORKSPACE_GRAPH_QUERY,
		operationName: teamsWorkspaceFilterOptionsOperationName,
		variables: {
			query: '',
			cloudIdARI: [`ari:cloud:confluence::site/${cloudId}`],
			limit: MAX_WORKSPACE_RESULTS,
			entities: [TEAMS_CONVERSATION_ENTITY_ATI],
			product: 'teams',
			integration: TEAMS_INTEGRATION_ARI,
			subtypes: ['space'],
		},
		aggAbsoluteUrl,
		headers,
	});

	if (response.errors?.length) {
		throw new Error(
			`[${teamsWorkspaceFilterOptionsOperationName}] GraphQL errors prevented loading MS Teams workspaces`,
		);
	}

	const seen = new Set<string>();
	return (
		response?.data?.search?.search?.edges?.flatMap(({ node }) => {
			const workspace = node?.entity?.workspace;
			if (!workspace || seen.has(workspace)) {
				return [];
			}
			seen.add(workspace);
			const result: MSTeamsWorkspace = {
				name: workspace,
				...(node?.entity?.id !== undefined && { id: node.entity.id }),
			};
			return [result];
		}) ?? []
	);
};
